import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Collapse,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";

// ─────────────────────────────────────────────────────────────────────────────
// LeadDataTable — drop-in replacement for MUI DataGrid
//
// Props mirror DataGrid's API so swapping is minimal in LeadTable:
//
//   rows            – array of row objects
//   columns         – array of column defs:
//                       { field, headerName, width?, minWidth?, flex?,
//                         sortable?, renderCell?(params) }
//   getRowId        – fn(row) => id  (defaults to row.id)
//   getRowClassName – fn(params) => className  (supports "even"/"odd")
//   loading         – boolean
//   pageSize        – number (default 10)
//   pageSizeOptions – ignored (kept for API compat)
//
//   // Expand/collapse:
//   expandedRowIds        – string[] controlled by parent
//   onExpandedRowIdsChange – fn(ids[]) => void
//
//   sx – ignored (styles are internal; kept for API compat)
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

const LeadDataTable = ({
  rows = [],
  columns = [],
  getRowId = (row) => row.id,
  getRowClassName,
  loading = false,
  expandedRowIds = [],
  onExpandedRowIdsChange,
}) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"
  const [hoverId, setHoverId] = useState(null);

  // ── Search across all string-valued fields ────────────────────────────────
  const searched = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((row) =>
      Object.values(row).some(
        (v) => v != null && String(v).toLowerCase().includes(q),
      ),
    );
  }, [rows, search]);

  // ── Sort ──────────────────────────────────────────────────────────────────
  const sorted = useMemo(() => {
    if (!sortField) return searched;
    return [...searched].sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [searched, sortField, sortDir]);

  // ── Pagination ────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));

  // Reset to page 1 whenever search/sort changes
  const safePageRows = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    return sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  }, [sorted, page, totalPages]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    setPage(1);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleToggleExpand = (id) => {
    if (!onExpandedRowIdsChange) return;
    onExpandedRowIdsChange(
      expandedRowIds.includes(id)
        ? expandedRowIds.filter((x) => x !== id)
        : [id], // only one row open at a time — change to [...expandedRowIds, id] for multi
    );
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const styles = {
    wrapper: {
      width: "100%",
      fontFamily: FONT,
      fontSize: 13.5,
      color: INK,
      background: "#FFFFFF",
      border: `1px solid ${LINE}`,
      borderRadius: "14px",
      overflow: "hidden",
    },
    toolbar: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 1,
      px: 1.75,
      py: 1.25,
      mb: 0,
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      tableLayout: "fixed",
    },
    th: {
      padding: "10px 12px",
      textAlign: "left",
      fontFamily: FONT,
      fontWeight: 700,
      fontSize: 10.5,
      letterSpacing: "0.09em",
      textTransform: "uppercase",
      color: MUTED,
      background: "#F7F9FC",
      borderTop: `1px solid ${LINE}`,
      borderBottom: `1px solid ${LINE}`,
      userSelect: "none",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    thInner: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      cursor: "pointer",
    },
    tdBase: {
      padding: "0 12px",
      borderBottom: "1px solid #F1F4F8",
      verticalAlign: "top",
      overflow: "hidden",
      fontFamily: FONT,
      fontSize: 13.5,
      color: INK_2,
    },
    tdInner: {
      minHeight: 50,
      display: "flex",
      alignItems: "center",
    },
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field)
      return (
        <UnfoldMoreIcon sx={{ fontSize: 15, color: "#B4BFCC", flexShrink: 0 }} />
      );
    return sortDir === "asc" ? (
      <ArrowUpwardIcon sx={{ fontSize: 14, color: PRIMARY, flexShrink: 0 }} />
    ) : (
      <ArrowDownwardIcon
        sx={{ fontSize: 14, color: PRIMARY, flexShrink: 0 }}
      />
    );
  };

  return (
    <Box sx={styles.wrapper}>
      {/* ── Toolbar ── */}
      <Box sx={styles.toolbar}>
        <TextField
          value={search}
          onChange={handleSearch}
          placeholder="Search…"
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            width: 300,
            maxWidth: "100%",
            "& .MuiOutlinedInput-root": {
              height: 36,
              borderRadius: "9px",
              fontFamily: FONT,
              fontSize: 13.5,
              background: "#FFFFFF",
            },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#B4C6DA" },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY, borderWidth: 1 },
          }}
        />
      </Box>

      {/* ── Table ── */}
      <Box sx={{ overflowX: "auto" }}>
        <table style={styles.table}>
          {/* HEAD */}
          <colgroup>
            {columns.map((col) => (
              <col
                key={col.field}
                style={{
                  width: col.width ? col.width : col.minWidth ? col.minWidth : 160,
                }}
              />
            ))}
          </colgroup>

          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.field}
                  style={{
                    ...styles.th,
                    color: sortField === col.field ? PRIMARY : MUTED,
                  }}
                >
                  {col.sortable === false ? (
                    <span>{col.headerName}</span>
                  ) : (
                    <div
                      style={styles.thInner}
                      onClick={() => handleSort(col.field)}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col.headerName}
                      </span>
                      <SortIcon field={col.field} />
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          {/* BODY */}
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  {columns.map((col) => (
                    <td key={col.field} style={styles.tdBase}>
                      <Box
                        sx={{
                          height: 14,
                          my: 2,
                          borderRadius: "6px",
                          bgcolor: i % 2 === 0 ? "#EDF1F5" : "#F4F7FA",
                          animation: "pulse 1.5s ease-in-out infinite",
                          "@keyframes pulse": {
                            "0%,100%": { opacity: 1 },
                            "50%": { opacity: 0.4 },
                          },
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : safePageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ ...styles.tdBase, textAlign: "center", padding: 36, borderBottom: 0 }}
                >
                  <Typography color="text.secondary" fontSize={14} sx={{ fontFamily: FONT, color: MUTED }}>
                    No rows found.
                  </Typography>
                </td>
              </tr>
            ) : (
              safePageRows.map((row, rowIdx) => {
                const id = getRowId(row);
                const isExpanded = expandedRowIds.includes(id);

                // getRowClassName mimics DataGrid's even/odd API
                const extraClass = getRowClassName
                  ? getRowClassName({
                      row,
                      indexRelativeToCurrentPage: rowIdx,
                    })
                  : "";
                const isEven = extraClass.includes("even");

                const rowBg = isExpanded
                  ? "#F4F8FC"
                  : hoverId === id
                    ? "#F4F7FA"
                    : isEven
                      ? "#FFFFFF"
                      : "#FCFDFE";

                return (
                  <React.Fragment key={id}>
                    <tr
                      style={{ background: rowBg, transition: "background .12s" }}
                      onMouseEnter={() => setHoverId(id)}
                      onMouseLeave={() => setHoverId(null)}
                    >
                      {columns.map((col) => {
                        // Intercept the expand column (field === "expand")
                        if (col.field === "expand") {
                          return (
                            <td key={col.field} style={{ ...styles.tdBase, background: rowBg, width: col.width || 60 }}>
                              <Box sx={styles.tdInner}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleExpand(id)}
                                  sx={{ color: MUTED, borderRadius: "8px", "&:hover": { background: "#EAF2FB", color: PRIMARY } }}
                                >
                                  {isExpanded ? (
                                    <ArrowUpwardIcon fontSize="small" />
                                  ) : (
                                    <ArrowDownwardIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Box>
                            </td>
                          );
                        }

                        const cellValue = row[col.field];
                        const rendered = col.renderCell
                          ? col.renderCell({
                              value: cellValue,
                              row,
                              field: col.field,
                            })
                          : cellValue != null
                            ? String(cellValue)
                            : "";

                        return (
                          <td
                            key={col.field}
                            style={{
                              ...styles.tdBase,
                              background: rowBg,
                              borderBottom: isExpanded ? 0 : styles.tdBase.borderBottom,
                            }}
                          >
                            <Box sx={styles.tdInner}>{rendered}</Box>
                          </td>
                        );
                      })}
                    </tr>

                    {/* Expand row — full width, same background */}
                    {isExpanded && (
                      <tr style={{ background: rowBg }}>
                        <td
                          colSpan={columns.length}
                          style={{
                            ...styles.tdBase,
                            background: rowBg,
                            padding: 0,
                          }}
                        >
                          <Collapse in={isExpanded} timeout="auto">
                            {/* Find and call the name column's renderCell which
                                already contains the expanded content */}
                            {(() => {
                              const nameCol = columns.find(
                                (c) => c.field === "name",
                              );
                              if (!nameCol?.renderExpandedContent) return null;
                              return nameCol.renderExpandedContent({ row });
                            })()}
                          </Collapse>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </Box>

      {/* ── Footer: count + pagination ── */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "space-between",
          alignItems: "center",
          mt: 0,
          px: 1.75,
          py: 1.25,
          borderTop: `1px solid ${LINE}`,
        }}
      >
        <Typography fontSize={12.5} color="text.secondary" sx={{ fontFamily: MONO, color: MUTED }}>
          {sorted.length} row{sorted.length !== 1 ? "s" : ""}
          {search ? ` (filtered from ${rows.length})` : ""}
        </Typography>
        <Pagination
          count={totalPages}
          page={Math.min(page, totalPages)}
          onChange={(_, p) => setPage(p)}
          size="small"
          shape="rounded"
          siblingCount={1}
          sx={{
            "& .MuiPaginationItem-root": {
              fontFamily: MONO,
              fontSize: 12.5,
              fontWeight: 600,
              color: INK_2,
              borderRadius: "8px",
              minWidth: 30,
              height: 30,
              "&:hover": { background: "#F4F7FA" },
            },
            "& .MuiPaginationItem-root.Mui-selected": {
              background: PRIMARY,
              color: "#FFFFFF",
              "&:hover": { background: "#084767" },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default LeadDataTable;
